import { useState, type FormEvent } from 'react';
import { createProveedor, updateProveedor } from '../../lib';
import type { FormaPagoSriProveedor, Proveedor, UpsertProveedorInput } from '../../lib/appTypes';

interface Props {
  initialProveedor?: Proveedor | null;
  onSaved: (proveedor: Proveedor) => void;
  onCancel: () => void;
}

const FORMAS_PAGO: Array<{ value: FormaPagoSriProveedor; label: string }> = [
  { value: 'TRANSFERENCIA_EFECTIVO', label: 'Transferencia / Efectivo' },
  { value: 'TARJETA_DEBITO_CREDITO', label: 'Tarjeta de débito o crédito' },
  { value: 'OTROS', label: 'Otros' },
];

const TIPOS_IDENTIFICACION = [
  { value: 'RUC', label: 'RUC' },
  { value: 'CEDULA', label: 'Cédula' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
] as const;

export default function FormProveedor({ initialProveedor, onSaved, onCancel }: Props) {
  const [form, setForm] = useState({
    tipoIdentificacion: initialProveedor?.tipoIdentificacion ?? 'RUC',
    identificacion: initialProveedor?.identificacion ?? '',
    nombreFiscal: initialProveedor?.nombreFiscal ?? '',
    ciudad: initialProveedor?.ciudad ?? '',
    direccion: initialProveedor?.direccion ?? '',
    telefono: initialProveedor?.telefono ?? '',
    email: initialProveedor?.email ?? '',
    formaPagoSri: initialProveedor?.formaPagoSri ?? 'TRANSFERENCIA_EFECTIVO',
    esPropia: initialProveedor?.esPropia ?? true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditing = Boolean(initialProveedor?.id);

  function handle(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name } = event.target;
    const value = event.target instanceof HTMLInputElement && event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');

    if (!form.identificacion.trim()) {
      setError('Ingresa la identificación del proveedor.');
      return;
    }

    if (!form.nombreFiscal.trim()) {
      setError('Ingresa el nombre fiscal del proveedor.');
      return;
    }

    const payload: UpsertProveedorInput = {
      tipoIdentificacion: form.tipoIdentificacion,
      identificacion: form.identificacion.trim(),
      nombreFiscal: form.nombreFiscal.trim(),
      ciudad: form.ciudad.trim(),
      direccion: form.direccion.trim(),
      telefono: form.telefono.trim(),
      email: form.email.trim(),
      formaPagoSri: form.formaPagoSri as FormaPagoSriProveedor,
      esPropia: form.esPropia,
    };

    try {
      setLoading(true);
      const proveedor = initialProveedor
        ? await updateProveedor(initialProveedor.id, payload)
        : await createProveedor(payload);

      onSaved(proveedor);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo guardar el proveedor.');
    } finally {
      setLoading(false);
    }
  }

  const inputCls =
    'w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-stone-900">
          {isEditing ? 'Editar proveedor' : 'Crear proveedor'}
        </h3>
        <p className="text-sm text-stone-500">
          Registra los datos fiscales y forma de pago del proveedor de insumos.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Tipo de identificación</label>
          <select
            name="tipoIdentificacion"
            value={form.tipoIdentificacion}
            onChange={handle}
            className={inputCls}
          >
            {TIPOS_IDENTIFICACION.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Identificación</label>
          <input
            name="identificacion"
            value={form.identificacion}
            onChange={handle}
            required
            className={inputCls}
            placeholder="1799999999001"
          />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Nombre fiscal</label>
          <input
            name="nombreFiscal"
            value={form.nombreFiscal}
            onChange={handle}
            required
            className={inputCls}
            placeholder="Proveedor S.A."
          />
        </div>

        <div>
          <label className={labelCls}>Ciudad</label>
          <input
            name="ciudad"
            value={form.ciudad}
            onChange={handle}
            className={inputCls}
            placeholder="Quito"
          />
        </div>

        <div>
          <label className={labelCls}>Teléfono</label>
          <input
            name="telefono"
            value={form.telefono}
            onChange={handle}
            className={inputCls}
            placeholder="0999999999"
          />
        </div>

        <div>
          <label className={labelCls}>Email</label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handle}
            className={inputCls}
            placeholder="proveedor@empresa.com"
          />
        </div>

        <div>
          <label className={labelCls}>Forma de pago SRI</label>
          <select
            name="formaPagoSri"
            value={form.formaPagoSri}
            onChange={handle}
            className={inputCls}
          >
            {FORMAS_PAGO.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-700">
            <input
              name="esPropia"
              type="checkbox"
              checked={form.esPropia}
              onChange={handle}
              className="mt-0.5 h-4 w-4 rounded border-stone-300 text-teal-700 focus:ring-teal-500"
            />
            <span>
              <span className="block font-semibold text-stone-900">Proveedor propio</span>
              <span className="block text-xs text-stone-500">
                Si está activo, los insumos de este proveedor no descontarán stock disponible al asignarse.
              </span>
            </span>
          </label>
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Dirección</label>
          <input
            name="direccion"
            value={form.direccion}
            onChange={handle}
            className={inputCls}
            placeholder="Dirección fiscal"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="ui-btn ui-btn-secondary"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="ui-btn ui-btn-primary"
        >
          {loading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear proveedor'}
        </button>
      </div>
    </form>
  );
}
